// R-05 (features 10/16): "Ask in your own words" on Browse. Typing "something chill outdoors Sunday" turns the page into the
// answer to that ask, ranked by their taste, through the existing `ask` function (one judge-tier call through
// _shared/model.ts, with its cost cap and its llm_calls log; no new model path). The words filter rides along so a word they
// hid never comes back. Clear brings Browse back. The last ask is kept for this tab, so Back from a detail or a row page
// returns to it.
import { useSyncExternalStore } from 'react'
import { ApiError, callFn, type AskResponse } from './api'
import type { Card } from './browse'
import { wordsNow } from './words'
import { onOwnerChange } from './owner'

export type AskState = { q: string; status: 'idle' | 'asking' | 'done' | 'error'; understood: string; cards: Card[]; loose: boolean; error: 'limit' | 'other' | null }
const IDLE: AskState = { q: '', status: 'idle', understood: '', cards: [], loose: false, error: null }
const KEY = 'mg_ask_browse'

function load(): AskState {
  try {
    const j = JSON.parse(sessionStorage.getItem(KEY) ?? 'null') as AskState | null
    return j && j.status === 'done' && typeof j.q === 'string' && Array.isArray(j.cards) ? j : IDLE
  } catch { return IDLE }
}
let state: AskState = typeof window === 'undefined' ? IDLE : load()
const subs = new Set<() => void>()
const set = (s: AskState) => {
  state = s
  try { if (s.status === 'done') sessionStorage.setItem(KEY, JSON.stringify(s)); else sessionStorage.removeItem(KEY) /* a failed or running ask never brings back an older answer on reload */ } catch { /* this view only */ }
  subs.forEach((f) => f())
}
export const useAskBrowse = () => useSyncExternalStore((f) => { subs.add(f); return () => { subs.delete(f) } }, () => state, () => IDLE)

/** An ask answer card as Browse draws it: the distance the ask measured, the time an event starts. */
const toCard = (h: AskResponse['items'][number]): Card => ({
  item: h.item, km: h.km ?? null, when: h.item.kind === 'event' && h.item.start_at ? { start: h.item.start_at, end: h.item.end_at ?? null } : null, added: false,
})

let seq = 0
export async function askBrowse(q: string, plan?: string | null): Promise<void> {
  const mine = ++seq
  set({ ...IDLE, q, status: 'asking', cards: state.cards, understood: state.understood })
  const { inc, exc } = wordsNow()
  try {
    const res = await callFn<AskResponse>('ask', { q, ...(plan ? { plan } : {}), ...(inc.length ? { include: inc } : {}), ...(exc.length ? { exclude: exc } : {}) })
    if (mine !== seq) return
    set({ q, status: 'done', understood: res.understood || q, cards: (res.items ?? []).map(toCard), loose: Boolean(res.loose), error: null })
  } catch (e) {
    if (mine !== seq) return
    set({ ...IDLE, q, status: 'error', error: e instanceof ApiError && e.status === 429 ? 'limit' : 'other' })
  }
}
export function clearAsk() { seq++; set(IDLE) }
// another person in this tab: their ask is not this person's
onOwnerChange(clearAsk)
